/*
 * Sesion del navegador (ADR-002 S2). El acceso dura 15 minutos y se renueva solo con el token de
 * renovacion, que rota en cada uso. Para no tocar las ~100 llamadas que ya existen (fetch y axios
 * con 'Bearer ' + localStorage.jwt armado a mano), se envuelven fetch y axios una sola vez:
 *   - antes de cada llamada a la API, si el acceso vence en menos de un minuto, se renueva;
 *   - se reemplaza la cabecera Authorization por el token vigente;
 *   - si la API responde 401, se intenta renovar una vez y se repite la llamada.
 * El jwt se sigue guardando en localStorage 'jwt' como JSON (igual que useLocalState).
 */
import axios from 'axios';
import { BASE_URL } from '../services/ApiService/ApiConfig';

const K_REFRESH = 'opem.refresh';
const K_EXPIRA = 'opem.jwtExpira';
const K_GEOCERCA = 'opem.geocerca';
const K_UBICACION = 'opem.pedirUbicacion';
export const CABECERA_CLIENTE = { 'X-OPEM-Cliente': 'web' };

const fetchOriginal = window.fetch.bind(window);
let renovando = null;

export function tokenActual() {
    try {
        return (JSON.parse(localStorage.getItem('jwt')) || '').replaceAll('"', '');
    } catch (e) {
        return (localStorage.getItem('jwt') || '').replaceAll('"', '');
    }
}

export function guardarIngreso(r) {
    localStorage.setItem('jwt', JSON.stringify(r.jwt || ''));
    if (r.refreshToken) {
        localStorage.setItem(K_REFRESH, r.refreshToken);
        localStorage.setItem(K_EXPIRA, String(Date.now() + (r.minutos || 15) * 60000));
    }
}

function limpiar() {
    ['jwt', 'roles', 'username', 'userId', 'pantallas', K_REFRESH, K_EXPIRA, K_GEOCERCA, K_UBICACION].forEach((k) => {
        try { localStorage.removeItem(k); } catch (e) { /* noop */ }
    });
}

function irAlLogin() {
    window.location.hash = '#/login';
    window.location.reload();
}

function esApi(url) {
    if (!url) return false;
    return (BASE_URL && url.startsWith(BASE_URL)) || url.includes('/opem-service/');
}

function esIngreso(url) {
    return url.includes('/auth/authenticate') || url.includes('/auth/renovar') || url.includes('/auth/segundo-factor')
        || url.includes('/auth/salir');
}

/** Renueva el acceso. forzar=true: aunque no este por vencer (despues de un 401). */
export function renovar(forzar = false) {
    const refresh = localStorage.getItem(K_REFRESH);
    const expira = Number(localStorage.getItem(K_EXPIRA)) || 0;
    if (!refresh) return Promise.resolve(false);
    if (!forzar && Date.now() < expira - 60000) return Promise.resolve(true);
    if (!renovando) {
        renovando = fetchOriginal(`${BASE_URL}/auth/renovar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...CABECERA_CLIENTE },
            body: JSON.stringify({ refreshToken: refresh })
        })
            .then(async (res) => {
                if (!res.ok) throw new Error('sesion terminada');
                guardarIngreso(await res.json());
                return true;
            })
            .catch(() => {
                limpiar();
                irAlLogin();
                return false;
            })
            .finally(() => { renovando = null; });
    }
    return renovando;
}

function conTokenVigente(headers) {
    const token = tokenActual();
    if (!headers || !token) return headers;
    if (headers instanceof Headers) {
        const a = headers.get('Authorization');
        if (a && a.startsWith('Bearer ')) headers.set('Authorization', `Bearer ${token}`);
        return headers;
    }
    const copia = { ...headers };
    Object.keys(copia).forEach((k) => {
        if (k.toLowerCase() === 'authorization' && String(copia[k]).startsWith('Bearer ')) copia[k] = `Bearer ${token}`;
    });
    return copia;
}

function instalarEnAxios(instancia) {
    instancia.interceptors.request.use(async (config) => {
        const url = (config.baseURL || '') + (config.url || '');
        if (esApi(url) && !esIngreso(url)) {
            await renovar();
            config.headers = conTokenVigente(config.headers);
        }
        return config;
    });
    instancia.interceptors.response.use(undefined, async (error) => {
        const config = error && error.config;
        const url = config ? (config.baseURL || '') + (config.url || '') : '';
        if (error && error.response && error.response.status === 401 && config && !config.__reintento
            && esApi(url) && !esIngreso(url) && localStorage.getItem(K_REFRESH)) {
            config.__reintento = true;
            if (await renovar(true)) {
                config.headers = conTokenVigente(config.headers);
                return instancia.request(config);
            }
        }
        return Promise.reject(error);
    });
}

let instalado = false;

/** Se llama una vez al arrancar la app (index.js) y para cada instancia de axios creada aparte. */
export function instalarSesion(...instanciasAxios) {
    instanciasAxios.forEach(instalarEnAxios);
    if (instalado) return;
    instalado = true;
    instalarEnAxios(axios);
    window.fetch = async (input, init = {}) => {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (!esApi(url) || esIngreso(url)) return fetchOriginal(input, init);
        await renovar();
        const pedido = { ...init, headers: conTokenVigente(init.headers) };
        let res = await fetchOriginal(input, pedido);
        if (res.status === 401 && localStorage.getItem(K_REFRESH) && (await renovar(true))) {
            res = await fetchOriginal(input, { ...init, headers: conTokenVigente(init.headers) });
        }
        return res;
    };
    iniciarGeocerca();
}

/** Cierra la sesion en el servidor y en el navegador. */
export async function salir() {
    const refresh = localStorage.getItem(K_REFRESH);
    try {
        await fetchOriginal(`${BASE_URL}/auth/salir`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenActual()}` },
            body: JSON.stringify({ refreshToken: refresh })
        });
    } catch (e) { /* sin red: igual se limpia el navegador */ }
    limpiar();
}

// ---------------------------------------------------------------- geocerca (S5)

export function ubicacionNavegador(msMax = 8000) {
    return new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
            (p) => resolve({ latitud: p.coords.latitude, longitud: p.coords.longitude }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: msMax, maximumAge: 60000 }
        );
    });
}

export function marcarGeocerca(exige) {
    if (exige) localStorage.setItem(K_GEOCERCA, '1'); else localStorage.removeItem(K_GEOCERCA);
}

/** Mapa de sesiones (decision 20/10): pedir la ubicacion una sola vez, en la primera pantalla despues de ingresar. */
export function marcarUbicacion(pedir) {
    if (pedir) localStorage.setItem(K_UBICACION, '1'); else localStorage.removeItem(K_UBICACION);
}

async function informarUbicacionUnaVez() {
    if (localStorage.getItem(K_UBICACION) !== '1' || !tokenActual()) return;
    localStorage.removeItem(K_UBICACION); // una sola vez por ingreso, acepte o no el usuario
    if (localStorage.getItem(K_GEOCERCA) === '1') return; // la geocerca ya informa la ubicacion cada pocos minutos
    const u = await ubicacionNavegador();
    if (!u) return; // no la dio: el mapa usa la sede de su IP
    try {
        await fetchOriginal(`${BASE_URL}/seguridad/mi/ubicacion`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenActual()}` },
            body: JSON.stringify(u)
        });
    } catch (e) { /* sin red: queda sin ubicacion */ }
}

let aviso = null;
function mostrarFuera(fuera) {
    if (fuera && !aviso) {
        aviso = document.createElement('div');
        aviso.setAttribute('role', 'alertdialog');
        aviso.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(15,23,42,.88);color:#fff;'
            + 'display:flex;align-items:center;justify-content:center;text-align:center;padding:24px;font-size:1.1rem';
        aviso.innerHTML = '<div><i class="pi pi-map-marker" style="font-size:2.5rem"></i><h2>Fuera de la zona autorizada</h2>'
            + '<p>Su usuario solo puede usar OPEM dentro de su zona de trabajo.<br>La sesion se reanuda al volver.</p></div>';
        document.body.appendChild(aviso);
    } else if (!fuera && aviso) {
        aviso.remove();
        aviso = null;
    }
}

async function informarUbicacion() {
    if (localStorage.getItem(K_GEOCERCA) !== '1' || !tokenActual()) return;
    const u = await ubicacionNavegador();
    try {
        const res = await fetchOriginal(`${BASE_URL}/seguridad/mi/ubicacion`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenActual()}` },
            body: JSON.stringify(u || {})
        });
        if (res.ok) mostrarFuera(!(await res.json()).dentro);
    } catch (e) { /* sin red: se reintenta */ }
}

let ultimaUbicacion = 0;
function iniciarGeocerca() {
    // cada 5 minutos; cada 30 s mientras este fuera, para reanudar apenas vuelva
    setInterval(() => {
        if (aviso || Date.now() - ultimaUbicacion >= 5 * 60000) {
            ultimaUbicacion = Date.now();
            informarUbicacion();
        }
    }, 30000);
    window.addEventListener('hashchange', informarUbicacion);
    window.addEventListener('hashchange', informarUbicacionUnaVez);
    informarUbicacion();
    informarUbicacionUnaVez();
}
